import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Bot, Send, X, ChevronRight, Sparkles, Beaker, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = window.__API_BASE;

const RECIPE_QUICK_ACTIONS = [
  { label: 'Suggest a recipe for my use case', prompt: 'I need help finding the right recipe for my project. What do you recommend?' },
  { label: 'What VFX recipes do you have?', prompt: 'Show me all VFX and post-production recipes available.' },
  { label: 'How do recipes work?', prompt: 'Explain how recipes work in Eipicenter — how do I run one and what do I get?' },
];

export default function RecipeAssistant({ currentRecipe, recipes, user }) {
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: currentRecipe
      ? `Hi! I'm your recipe assistant. You're viewing **${currentRecipe.name}** — ask me anything about how it works, what inputs to use, or how to get the best results.`
      : `Hi! I'm your recipe assistant. I know all ${recipes?.length || 29} recipes — ask me to suggest one, explain how they work, or compare options.`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (showChat) {
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [messages, showChat]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const context = {};
      if (currentRecipe) {
        context.recipe_name = currentRecipe.name;
        context.recipe_slug = currentRecipe.slug;
        context.recipe_category = currentRecipe.category;
        context.recipe_description = currentRecipe.description;
        context.recipe_inputs = currentRecipe.inputs?.map(i => i.label).join(', ');
        context.recipe_credits = currentRecipe.pricing?.credits;
      }
      if (recipes?.length) {
        context.available_recipes = recipes.map(r => `${r.name} (${r.category})`).join(', ');
      }
      const { data } = await axios.post(`${API}/api/recipes/chat`, {
        session_id: currentRecipe ? `recipe_${currentRecipe.slug}` : 'recipes_gallery',
        message: msg,
        context,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response || 'I can help with recipes — try asking about a specific one.',
        suggestions: data.suggestions,
      }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I had trouble processing that. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Chat Panel */}
      {showChat && (
        <aside
          className="fixed inset-y-0 right-0 z-50 w-80 flex flex-col transition-transform duration-300 ease-in-out translate-x-0"
          style={{ background: '#0B0F19', borderLeft: '1px solid rgba(14, 165, 233, 0.15)' }}
          data-testid="recipe-assistant-panel"
        >
          <div className="fixed inset-0 bg-black/50 z-[-1] lg:hidden" onClick={() => setShowChat(false)} />
          {/* Header */}
          <div className="h-14 px-4 border-b border-white/[0.06] flex items-center justify-between" style={{ background: 'rgba(19, 27, 44, 0.5)' }}>
            <div className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_a2435b19-3cc0-4261-90b4-1cf9d208aea9/artifacts/h74r9xmb_image.png" alt="EIPI Bot" className="w-7 h-7 rounded-sm object-cover border border-white/[0.06] flex-shrink-0" />
              <span className="font-mono text-sm font-medium text-white/90 tracking-tight">RECIPE_ASSIST</span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-[10px] font-mono text-green-400">ONLINE</span>
              </span>
            </div>
            <button onClick={() => setShowChat(false)} className="p-1.5 hover:bg-white/10 rounded-sm transition-colors" data-testid="close-recipe-assistant">
              <ChevronRight className="w-4 h-4 text-white/50" />
            </button>
          </div>
          {/* Context bar */}
          <div className="h-10 px-4 border-b border-white/[0.06] flex items-center" style={{ background: 'rgba(19, 27, 44, 0.3)' }}>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-sm bg-[#0EA5E9]" />
              <span className="text-[10px] font-mono font-medium text-white/40 uppercase tracking-wider">
                {currentRecipe ? currentRecipe.name : 'Recipe Gallery'}
              </span>
            </div>
          </div>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
            {messages.map((msg, idx) => (
              <div key={idx} className={msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-sm flex items-center justify-center mr-2 flex-shrink-0" style={{ background: 'rgba(14, 165, 233, 0.2)', border: '1px solid rgba(14, 165, 233, 0.3)' }}>
                    <Bot className="w-4 h-4 text-[#0EA5E9]" />
                  </div>
                )}
                <div className={`p-3 max-w-[85%] ${msg.role === 'user' ? 'rounded-xl rounded-tr-none ml-auto' : 'rounded-sm rounded-tl-none'}`}
                  style={msg.role === 'user'
                    ? { background: 'rgba(212, 163, 115, 0.08)', border: '1px solid rgba(212, 163, 115, 0.2)' }
                    : { background: 'rgba(14, 165, 233, 0.08)', border: '1px solid rgba(14, 165, 233, 0.2)' }
                  }>
                  {msg.role === 'user' ? (
                    <p className="text-sm" style={{ color: '#E8DCCA' }}>{msg.content}</p>
                  ) : (
                    <div className="text-sm text-white/80 prose prose-invert prose-sm max-w-none
                      [&_p]:mb-2 [&_p:last-child]:mb-0
                      [&_ul]:mb-2 [&_ul]:pl-4 [&_ul]:list-disc
                      [&_li]:mb-1 [&_strong]:text-[#0EA5E9] [&_strong]:font-semibold
                      [&_code]:bg-white/5 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded-sm [&_code]:text-[#0EA5E9] [&_code]:text-xs">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </div>
                  )}
                  {msg.suggestions && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {msg.suggestions.map((sug, i) => (
                        <button key={i} onClick={() => { setInput(sug); sendMessage(sug); }}
                          className="px-2.5 py-1 text-[11px] rounded-sm transition-colors font-mono text-white/50 hover:text-white"
                          style={{ background: 'rgba(14, 165, 233, 0.1)', border: '1px solid rgba(14, 165, 233, 0.2)' }}>
                          {sug}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center ml-2 flex-shrink-0" style={{ background: 'rgba(212, 163, 115, 0.2)', border: '1px solid rgba(212, 163, 115, 0.3)' }}>
                    <span className="text-xs" style={{ color: '#D4A373' }}>{user?.name?.[0] || 'U'}</span>
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ background: 'rgba(14, 165, 233, 0.2)' }}>
                  <Bot className="w-4 h-4 text-[#0EA5E9]" />
                </div>
                <div className="rounded-sm rounded-tl-none p-3 flex items-center gap-2" style={{ background: 'rgba(14, 165, 233, 0.1)', border: '1px solid rgba(14, 165, 233, 0.2)' }}>
                  <Loader2 className="w-4 h-4 animate-spin text-[#0EA5E9]" />
                  <span className="text-sm font-mono text-white/40">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          {/* Quick Actions */}
          <div className="px-4 py-3 border-t border-white/[0.06]">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-sm bg-[#0EA5E9]" />
              <span className="text-[10px] font-mono font-medium text-white/40 uppercase tracking-wider">Quick Actions</span>
            </div>
            <div className="space-y-1.5">
              {(currentRecipe ? [
                { label: `Best inputs for ${currentRecipe.name}?`, prompt: `What are the best inputs and settings for ${currentRecipe.name}? Give me tips for getting the best results.` },
                { label: 'Similar recipes', prompt: `What other recipes are similar to ${currentRecipe.name}? How do they compare?` },
              ] : RECIPE_QUICK_ACTIONS.slice(0, 2)).map((action, i) => (
                <button key={i} onClick={() => sendMessage(action.prompt)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-sm transition-colors font-mono text-xs text-white/50 hover:text-white"
                  style={{ background: i === 0 ? 'rgba(14, 165, 233, 0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${i === 0 ? 'rgba(14, 165, 233, 0.15)' : 'rgba(255,255,255,0.06)'}` }}>
                  {i === 0 ? <Sparkles className="w-3.5 h-3.5 text-[#0EA5E9]" /> : <Beaker className="w-3.5 h-3.5 text-white/30" />}
                  <span className="truncate">{action.label}</span>
                </button>
              ))}
            </div>
          </div>
          {/* Input */}
          <div className="p-4 border-t border-white/[0.06]">
            <div className="relative">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={currentRecipe ? `Ask about ${currentRecipe.name}...` : 'Ask about any recipe...'}
                className="w-full px-4 py-3 pr-14 rounded-sm text-sm font-mono text-white/90"
                style={{ background: 'rgba(19, 27, 44, 0.6)', border: '1px solid rgba(14, 165, 233, 0.2)' }}
                data-testid="recipe-assistant-input"
              />
              <button onClick={() => sendMessage()} disabled={!input.trim() || loading}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-sm transition-colors disabled:opacity-50"
                style={{ background: '#0EA5E9' }}
                data-testid="recipe-assistant-send">
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </aside>
      )}

      {/* Toggle button */}
      {!showChat && (
        <button
          onClick={() => setShowChat(true)}
          data-testid="open-recipe-assistant"
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center z-50 hover:scale-110 transition-all duration-300 overflow-hidden shadow-lg shadow-[#0EA5E9]/20"
          style={{ background: '#0EA5E9' }}
        >
          <img src="https://customer-assets.emergentagent.com/job_a2435b19-3cc0-4261-90b4-1cf9d208aea9/artifacts/h74r9xmb_image.png" alt="EIPI Bot" className="w-10 h-10 rounded-full object-cover" />
        </button>
      )}
    </>
  );
}
